import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Instagram, Twitter } from "lucide-react";
import { api, imgSrc } from "@/lib/api";

export default function PersonDetail({ type }) {
  const { id } = useParams();
  const [person, setPerson] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPerson(null);
    setNotFound(false);
    api
      .get(`/content/${type}/${id}`)
      .then((r) => setPerson(r.data))
      .catch(() => setNotFound(true));
  }, [type, id]);

  const backTo = type === "cast" ? "/about" : "/spotlight";
  const backLabel = type === "cast" ? "Back to about" : "Back to spotlight";

  if (notFound) {
    return (
      <div className="py-40 text-center" data-testid="person-not-found">
        <p className="font-display text-3xl font-black text-slate-900">Profile not found</p>
        <Link to={backTo} className="link-underline mt-6" data-testid="person-back-link">
          <ArrowLeft className="h-4 w-4" /> {backLabel}
        </Link>
      </div>
    );
  }

  if (!person) {
    return <div className="py-40 text-center text-slate-400 text-xs tracking-[0.25em] uppercase">Loading…</div>;
  }

  return (
    <div data-testid="person-detail-page">
      <section className="py-16 sm:py-24 bg-white">
        <div className="container-site grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="aspect-[3/4] overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-100 via-cyan-50 to-pool-light/60 shadow-xl flex items-center justify-center"
          >
            {person.photo ? (
              <img src={imgSrc(person.photo)} alt={person.name} className="h-full w-full object-cover" />
            ) : (
              <div className="text-center px-6">
                <span className="font-script text-7xl text-pool/70 leading-none">{person.name?.split(" ").slice(0, 2).map((w) => w[0]).join("")}</span>
                <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-pool-deep/50 mt-4">Photo coming soon</p>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:pt-10"
          >
            <Link
              to={backTo}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-bold text-slate-400 hover:text-pool transition-colors mb-8"
              data-testid="person-back-link"
            >
              <ArrowLeft className="h-4 w-4" /> {backLabel}
            </Link>
            <p className="label-eyebrow mb-4" data-testid="person-role">
              {person.role || person.category}
            </p>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-slate-900" data-testid="person-name">
              {person.name}
            </h1>
            {person.descriptor && (
              <p className="mt-3 text-pool font-semibold" data-testid="person-descriptor">{person.descriptor}</p>
            )}
            {person.bio && (
              <p className="mt-8 text-slate-600 leading-relaxed text-base sm:text-lg" data-testid="person-bio">
                {person.bio}
              </p>
            )}

            {person.appearance_link && /\.(mp4|mov|webm)(\?|$)/i.test(person.appearance_link) && (
              <div className="mt-10 rounded-2xl overflow-hidden shadow-xl max-w-md" data-testid="person-appearance-video">
                <video src={imgSrc(person.appearance_link)} controls playsInline preload="metadata" className="w-full aspect-video bg-ink" />
              </div>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-6">
              {person.appearance_link && !/\.(mp4|mov|webm)(\?|$)/i.test(person.appearance_link) && (
                <a
                  href={person.appearance_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-pool hover:bg-pool-dark text-white text-xs tracking-[0.15em] uppercase font-bold px-6 py-3.5 rounded-full transition-colors"
                  data-testid="person-appearance-link"
                >
                  Watch the appearance <ArrowUpRight className="h-4 w-4" />
                </a>
              )}
              {person.instagram && (
                <a href={person.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-slate-400 hover:text-pool transition-colors" data-testid="person-instagram">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {person.twitter && (
                <a href={person.twitter} target="_blank" rel="noreferrer" aria-label="Twitter" className="text-slate-400 hover:text-pool transition-colors" data-testid="person-twitter">
                  <Twitter className="h-5 w-5" />
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
